import { forwardRef, useId, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { useFormatters } from '../../services/formatters/FormattersProvider';
import type { DateInput } from '../../services/formatters/formatters';
import { formatRoutePoint, type RoutePoint } from '../RouteDisplay/RouteDisplay';
import { useMapProvider } from './MapProviderContext';
import { useDsT } from '../../services/i18n';

export interface MapPoint extends RoutePoint {
  /** Planned or actual time at this point. */
  at?: DateInput;
  /** Longitude / latitude, for a real provider; unused by the summary. */
  lng?: number;
  lat?: number;
}

export interface MapRoute {
  origin: MapPoint;
  destination: MapPoint;
  via?: MapPoint[];
}

export interface MapPosition {
  /** Where the shipment is now, as words (`Pacific Ocean`, `Rotterdam terminal`). */
  label: string;
  at?: DateInput;
  /** Index of the last route point passed (0 = origin); the marker sits between it and the next one. */
  afterIndex?: number;
  /** 0–1 along the whole route; used when `afterIndex` is absent. */
  progress?: number;
  lng?: number;
  lat?: number;
}

export type MapMarkerKind = 'origin' | 'destination' | 'via' | 'position' | 'poi' | 'alert';

export interface MapMarker {
  id: string;
  label: string;
  kind?: MapMarkerKind;
  lng?: number;
  lat?: number;
}

/** A coordinate pair (WGS 84 degrees). */
export interface MapLatLng {
  lat: number;
  lng: number;
}

/**
 * One of several routes on the same map (G-527: a fleet plan's vehicles). Points in travel order; each layer takes a
 * chart series colour (`series` 1–8, default by its position) and appears in the legend with its label.
 */
export interface MapRouteLayer {
  id: string;
  label: string;
  points: Array<MapLatLng & { label?: string }>;
  series?: number;
}

export type MapShapeTone = 'zone' | 'accent' | 'warning' | 'danger';

/** An area drawn on the map (G-527: geofences) — a circle (centre + radius in metres) or a polygon (corners in order). */
export type MapShape =
  | { id: string; label?: string; tone?: MapShapeTone; kind: 'circle'; center: MapLatLng; radiusM: number }
  | { id: string; label?: string; tone?: MapShapeTone; kind: 'polygon'; points: MapLatLng[] };

export interface MapViewProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  route?: MapRoute;
  /** Several routes on one map, each in its own colour (drawn by a map provider; the summary lists them). */
  routes?: MapRouteLayer[];
  /** Areas (circles, polygons) under the routes and markers. */
  shapes?: MapShape[];
  position?: MapPosition;
  markers?: MapMarker[];
  /** Pixels (default 240). */
  height?: number;
  /** Pan / zoom for a real provider; the summary ignores it. */
  interactive?: boolean;
  /** Shown under the summary while no map provider is installed. */
  unavailableMessage?: ReactNode;
  /** Accessible name of the figure. Default `Route map`. */
  title?: string;
  /** Prefix for the current position. Default `Now`. */
  positionLabel?: string;
  /** Rendered when there is no route at all. Default `No route to show`. */
  emptyLabel?: string;
}

const W = 640;
const PAD_X = 48;

const SERIES_BG = ['bg-chart-1', 'bg-chart-2', 'bg-chart-3', 'bg-chart-4', 'bg-chart-5', 'bg-chart-6', 'bg-chart-7', 'bg-chart-8'];
const SERIES_STROKE = ['stroke-chart-1', 'stroke-chart-2', 'stroke-chart-3', 'stroke-chart-4', 'stroke-chart-5', 'stroke-chart-6', 'stroke-chart-7', 'stroke-chart-8'];
const SERIES_FILL = ['fill-chart-1', 'fill-chart-2', 'fill-chart-3', 'fill-chart-4', 'fill-chart-5', 'fill-chart-6', 'fill-chart-7', 'fill-chart-8'];
const seriesIndex = (series: number) => (((Math.round(series) - 1) % 8) + 8) % 8;
/** Token classes of chart series 1–8 (wraps), for route layers and their legend. */
export const seriesBg = (series: number) => SERIES_BG[seriesIndex(series)]!;
export const seriesStroke = (series: number) => SERIES_STROKE[seriesIndex(series)]!;
export const seriesFill = (series: number) => SERIES_FILL[seriesIndex(series)]!;

/** Route points in travel order. */
export function routeStops(route: MapRoute | undefined): MapPoint[] {
  return route ? [route.origin, ...(route.via ?? []), route.destination] : [];
}

/** Fraction 0–1 of the route line the position marker sits at. */
export function positionFraction(position: MapPosition | undefined, stopCount: number): number | null {
  if (!position || stopCount < 2) return null;
  const segments = stopCount - 1;
  if (position.afterIndex != null) {
    const i = Math.max(0, Math.min(segments - 1, position.afterIndex));
    return (i + 0.5) / segments;
  }
  if (position.progress != null) return Math.max(0, Math.min(1, position.progress));
  return 0.5;
}

/**
 * Where the shipment is (D-006 degraded mode): a token-styled SVG route summary — origin, via points and
 * destination on a line, the current position marker, country codes and times — with the real map behind a
 * provider context. Light / dark through the `map-*` and text tokens.
 */
export const MapView = forwardRef<HTMLDivElement, MapViewProps>(function MapView(props, ref) {
  const provider = useMapProvider();
  const { route, routes = [], shapes = [], position, markers = [], height = 240, interactive, unavailableMessage: unavailableMessageProp, title: titleProp, positionLabel: positionLabelProp, emptyLabel: emptyLabelProp, className, ...rest } = props;
  const f = useFormatters();
  const id = useId();
  const t = useDsT();
  const unavailableMessage = unavailableMessageProp === undefined ? t('ds.mapView.unavailable', 'Live map unavailable — showing the route summary') : unavailableMessageProp;
  const title = titleProp ?? t('ds.mapView.title', 'Route map');
  const positionLabel = positionLabelProp ?? t('ds.mapView.now', 'Now');
  const emptyLabel = emptyLabelProp ?? t('ds.mapView.empty', 'No route to show');
  const stops = routeStops(route);
  const fraction = positionFraction(position, stops.length);
  const H = Math.max(160, height);
  const lineY = Math.round(H * 0.5);
  const span = W - PAD_X * 2;
  const xAt = (i: number) => (stops.length < 2 ? W / 2 : PAD_X + (span * i) / (stops.length - 1));
  const posX = fraction === null ? null : PAD_X + span * fraction;
  const summary = stops.length
    ? `${title}: ${stops.map(formatRoutePoint).join(' → ')}${position ? `. ${positionLabel}: ${position.label}` : ''}`
    : `${title}: ${emptyLabel}`;
  void interactive;

  // A provider places points by their coordinates; with none to place, the route summary says more than an empty map.
  const placeable =
    [...stops, ...(position ? [position] : []), ...markers].some((p) => typeof p.lat === 'number' && typeof p.lng === 'number') ||
    routes.some((r) => r.points.length > 0) ||
    shapes.length > 0;
  if (provider && placeable) {
    return (
      <div ref={ref} data-map-provider={provider.name} className={cn('zo-map-view relative overflow-hidden rounded-lg', className)} {...rest}>
        {provider.render(props)}
      </div>
    );
  }

  return (
    <figure ref={ref} data-map-provider="summary" aria-labelledby={`${id}-title`} className={cn('zo-map-view m-0 flex flex-col overflow-hidden rounded-lg border border-border bg-map-land text-fg', className)} {...rest}>
      <figcaption id={`${id}-title`} className="sr-only">
        {summary}
      </figcaption>
      {stops.length ? (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="xMidYMid meet" role="img" aria-label={summary} className="block">
          <line x1={xAt(0)} y1={lineY} x2={xAt(stops.length - 1)} y2={lineY} strokeWidth={3} strokeLinecap="round" className="stroke-map-route" />
          {posX !== null ? <line x1={xAt(0)} y1={lineY} x2={posX} y2={lineY} strokeWidth={3} strokeLinecap="round" className="stroke-map-route-active" data-progress /> : null}
          {stops.map((stop, i) => {
            const x = xAt(i);
            const end = i === 0 || i === stops.length - 1;
            const anchor = i === 0 ? 'start' : i === stops.length - 1 ? 'end' : 'middle';
            const tx = i === 0 ? x - 10 : i === stops.length - 1 ? x + 10 : x;
            return (
              <g key={`${stop.name}-${i}`} data-stop={i} data-stop-kind={i === 0 ? 'origin' : end ? 'destination' : 'via'}>
                <circle cx={x} cy={lineY} r={end ? 8 : 5} strokeWidth={2} className={cn(end ? 'fill-map-node stroke-map-land' : 'fill-map-land stroke-map-route')} />
                <text x={tx} y={lineY - 20} textAnchor={anchor} fontSize={13} fontWeight={600} className="fill-fg">
                  {stop.name}
                </text>
                {stop.countryCode || stop.code ? (
                  <text x={tx} y={lineY - 6} textAnchor={anchor} fontSize={11} className="fill-fg-muted font-mono">
                    {[stop.countryCode?.toUpperCase(), stop.code].filter(Boolean).join(' · ')}
                  </text>
                ) : null}
                {stop.at != null ? (
                  <text x={tx} y={lineY + 24} textAnchor={anchor} fontSize={11} className="fill-fg-muted tnum">
                    {f.dateTime(stop.at)}
                  </text>
                ) : null}
              </g>
            );
          })}
          {posX !== null && position ? (
            <g data-position transform={`translate(${posX} ${lineY})`}>
              <circle r={11} className="fill-accent/20" />
              <circle r={6} strokeWidth={2} className="fill-accent stroke-map-land" />
              <text y={posX > W / 2 ? 44 : 44} x={0} textAnchor="middle" fontSize={12} fontWeight={600} className="fill-accent-fg">
                {position.label}
              </text>
              {position.at != null ? (
                <text y={60} textAnchor="middle" fontSize={11} className="fill-fg-muted tnum">
                  {f.dateTime(position.at)}
                </text>
              ) : null}
            </g>
          ) : null}
        </svg>
      ) : (
        <div className="flex items-center justify-center gap-2 type-body text-fg-muted" style={{ height: H }}>
          <Icon name="map" size={20} aria-hidden="true" />
          {emptyLabel}
        </div>
      )}
      {markers.length || routes.length || shapes.length ? (
        <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 border-t border-border bg-surface-1 px-3 py-2 type-caption text-fg-muted" aria-label={title}>
          {routes.map((r, i) => (
            <li key={`route-${r.id}`} data-route-layer={r.id} className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className={cn('inline-block h-1 w-4 rounded-full', seriesBg(r.series ?? i + 1))} />
              {r.label}
            </li>
          ))}
          {shapes.map((sh) => (
            <li key={`shape-${sh.id}`} data-shape-kind={sh.kind} className="inline-flex items-center gap-1.5">
              <Icon name={sh.kind === 'circle' ? 'circle-dot' : 'square'} size={12} aria-hidden="true" className="text-fg-subtle" />
              {sh.label ?? sh.id}
            </li>
          ))}
          {markers.map((m) => (
            <li key={m.id} data-marker-kind={m.kind ?? 'poi'} className="inline-flex items-center gap-1.5">
              <Icon name={m.kind === 'alert' ? 'triangle-alert' : 'map-pin'} size={12} aria-hidden="true" className={cn(m.kind === 'alert' ? 'text-danger-fg' : m.kind === 'position' ? 'text-accent-fg' : 'text-fg-subtle')} />
              {m.label}
            </li>
          ))}
        </ul>
      ) : null}
      {unavailableMessage ? (
        <div className="flex items-center gap-2 border-t border-border bg-surface-1 px-3 py-1.5 type-caption text-fg-muted" data-unavailable>
          <Icon name="cloud-off" size={12} aria-hidden="true" />
          {unavailableMessage}
        </div>
      ) : null}
    </figure>
  );
});
